import { discontinuedGoalsAsValue } from '@/utils/goals-map';
import { discontinuedFragrancesAsValue } from '@/utils/fragrance-map';

export default class OrdersContext extends HTMLElement {
  constructor() {
    super();
  }

  ALLOWED_PRODUCTS = [
    'conditioner',
    'hair-mask',
    'hair-serum',
    'leave-in-conditioner',
    'purple-conditioner',
    'purple-hair-mask',
    'purple-shampoo',
    'shampoo',
    'styling-primer',
    'sos-conditioner',
    'sos-hair-mask',
    'sos-leave-in-treatment',
    'sos-shampoo',
    'body-wash',
    'body-lotion'
  ];

  DISCONTINUED_COLORS = ['Black', 'Sunkissed'];
  DISCONTINUED_SIZES = ['16 oz', '8 oz'];
  REPLACEMENT_COLOR = 'Function Green';

  DEFAULT_FRAGRANCE = 'fragrance-free';
  DEFAULT_PROFILE_NAME = 'of beauty';
  DEFAULT_GOALS = 'hydrate';
  DEFAULT_BODY_MOISTURE = 'balanced';

  selectOrder(orderId) {
    const selectedOrder = this.orders.find((order) => order.orderId == orderId);
    const formattedOrder = {
      ...selectedOrder,
      lineItems: (selectedOrder.lineItems ?? [])
        .filter((item) => this.ALLOWED_PRODUCTS.includes(item?.handle))
        .map((lineItem) => ({
          ...this.validateLineItem(lineItem),
          properties: this.formatProperties(lineItem.properties)
        }))
    };

    const name =
      formattedOrder?.lineItems[0]?.properties?.__profile_name ||
      this.DEFAULT_PROFILE_NAME;

    const goalsFromAllItems = formattedOrder?.lineItems.reduce((acc, item) => {
      const itemGoals = item.properties?.__hair_goals || '';
      if (itemGoals) {
        const goalsArray = itemGoals.split(',').map((goal) => goal.trim());
        goalsArray.forEach((goal) => {
          if (!acc.includes(goal)) {
            acc.push(goal);
          }
        });
      }
      return acc;
    }, []);

    const goals = goalsFromAllItems?.length
      ? goalsFromAllItems.join(',')
      : this.DEFAULT_GOALS;

    // const fragrance =
    //   formattedOrder?.lineItems[0]?.properties?.__fragrance ||
    //   this.DEFAULT_FRAGRANCE;
    const items = formattedOrder?.lineItems || '';
    const limitedFragrances = items?.find((item) =>
      item.handle?.includes('sos')
    )
      ? '1'
      : '0';
    const isOnlyHairProducts = items?.every(
      (item) => item.profileType === 'hair'
    );
    const isOnlyBodyProducts = items?.every(
      (item) => item.profileType === 'body'
    );
    let profileType = '';
    if (isOnlyHairProducts) {
      profileType = 'hair';
    } else if (isOnlyBodyProducts) {
      profileType = 'body';
    } else {
      profileType = 'mixed';
    }

    const hairFragrance =
      items?.find((item) => item.properties?.__profile_type === 'hair')
        ?.properties?.__fragrance || '';
    const bodyFragrance =
      items?.find((item) => item.properties?.__profile_type === 'body')
        ?.properties?.__fragrance || '';
    const bodyMoisture =
      formattedOrder?.lineItems[0]?.properties?.__body_moisture ||
      this.DEFAULT_BODY_MOISTURE;

    return {
      name,
      goals,
      hairFragrance,
      bodyFragrance,
      items,
      limitedFragrances,
      profileType,
      bodyMoisture
    };
  }

  hasDiscontinuedAttributes(orderId) {
    const order = this.orders.find((order) => order.orderId == orderId);
    const hasDiscontinuedGoals = order.lineItems.some((item) =>
      discontinuedGoalsAsValue.some((goal) =>
        this.formatProperties(item.properties)
          ?.__hair_goals?.split(',')
          .includes(goal)
      )
    );

    const hasDiscontinuedFragrances = order.lineItems.some((item) =>
      discontinuedFragrancesAsValue.includes(
        this.formatProperties(item.properties)?.__fragrance
      )
    );

    return {
      hasDiscontinuedFragrances,
      hasDiscontinuedGoals,
      hasDiscontinuedAttributes:
        hasDiscontinuedGoals || hasDiscontinuedFragrances
    };
  }

  connectedCallback() {
    this.customizationDetails = this.querySelector('customization-details');
  }

  get orders() {
    return JSON.parse(this.getAttribute('orders')).orders;
  }

  formatProperties(properties) {
    return properties.reduce((acc, property) => {
      acc[property[0]] = property[1];
      return acc;
    }, {});
  }

  validateLineItem(lineItem) {
    let newVariant = null;
    let currentColor = lineItem.color;
    let currentSize = lineItem.size;
    let profileType =
      lineItem.properties?.__profile_type ??
      this.getProductType(lineItem['product_type']);

    if (!lineItem.color || !lineItem.size) {
      newVariant = lineItem.variants?.find(
        (variant) =>
          !this.DISCONTINUED_COLORS.includes(variant.option2) &&
          !this.DISCONTINUED_SIZES.includes(variant.option1)
      );
    } else if (this.DISCONTINUED_COLORS.includes(currentColor)) {
      newVariant = lineItem.variants?.find(
        (variant) =>
          variant.option2 === this.REPLACEMENT_COLOR &&
          variant.option1 === currentSize
      );
    }
    lineItem = {
      ...lineItem,
      profileType
    };

    return newVariant
      ? {
          ...lineItem,
          size: newVariant.option1,
          color: newVariant.option2,
          variant_id: newVariant.id
        }
      : lineItem;
  }

  getProductType(type) {
    const typeMap = {
      Haircare: 'hair',
      Bodycare: 'body'
    };
    return typeMap[type] || 'skin';
  }
}
